import {
  type ColorFamily,
  colors,
  type SemanticColor,
  semanticNames,
  UI_THEMES,
  type UITheme,
} from '@kizen/kds/Colors';
import { useCallback, useMemo } from 'react';
import { type TFunction, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getCurrentTheme, getUIThemes } from 'store/authentication/selectors';

const getThemeOptions = (uiThemes: UITheme[], t: TFunction) => {
  const translatedThemes = {
    [UI_THEMES.LIGHT]: t('Light'),
    [UI_THEMES.HIGH_CONTRAST]: t('High Contrast'),
    [UI_THEMES.QA]: t('QA'),
    [UI_THEMES.BRAND]: t('Brand'),
  };
  return uiThemes.map((theme) => ({
    label: translatedThemes[theme] || theme,
    value: theme,
  }));
};

export const useTheme = () => {
  const { t } = useTranslation();
  const currentTheme = useSelector(getCurrentTheme) as UITheme;
  const uiThemes = useSelector(getUIThemes) as UITheme[];

  const themeOptions = useMemo(
    () => getThemeOptions(uiThemes, t),
    [uiThemes, t]
  );

  const getSemanticColorByTheme = useCallback(
    (semanticColor: SemanticColor, theme: UITheme) => {
      const colorName = semanticNames[semanticColor][theme];
      const color = colorName.split('-')[0] as ColorFamily;
      return colors[color][colorName];
    },
    []
  );

  const getSemanticColor = useCallback(
    (semanticColor: SemanticColor) => {
      return getSemanticColorByTheme(semanticColor, currentTheme);
    },
    [currentTheme, getSemanticColorByTheme]
  );

  return {
    currentTheme,
    uiThemes,
    themeOptions,
    getSemanticColorByTheme,
    getSemanticColor,
  };
};
